import { useRecoilState } from "recoil"
import { useQueryClient } from "react-query"
import { INFINITE_QUERY_KEY } from "../consts/request"
import { realTimeUpdatesState } from "../common/Refresher.state"

const getDetailEntity = ({ source, detail }) => {
    if (!detail) {
      return { entity: null, entityId: null, listKey: null }
    }
  
    // Get the entity type and the list key from the source
    // e.g. ss.comment.update -> comment, comments
    // e.g. ss.comment.like -> comment, comments
    // e.g. ss.task.update -> task, tasks, etc.
    const entity = source.split('.')[1]
    const listKey = `${entity}s`
    let entityId
  
    // If the entity is a game and the source includes 'media',
    // the entityId is the id of the media relation
    if (entity === 'game' && source.includes('media')) {
      entityId = detail.media?.relation?.id
    } else {
      entityId = detail[entity]?.id
    }
  
    return { entity, entityId, listKey }
}

// Apply in-place cache updates for instant update events.
// Returns true when the cache was updated (and invalidation should be skipped).
const handleInstantUpdateEvents = (queryClient, key, groupedUpdates) => {
    const gameExportUrl = groupedUpdates.reduce((acc, updateItem) => {
        const isGameExport = updateItem?.message?.source === 'ss.game.export'
        const exportUrl = updateItem?.message?.detail?.game?.export_url
        const exportFinishedAt = updateItem?.message?.detail?.game?.export_finished_at
        const exportEmails = updateItem?.message?.detail?.game?.export_emails
        return isGameExport && exportUrl && exportFinishedAt && exportEmails ? { exportUrl, exportFinishedAt, exportEmails } : acc
    }, null)

    if (gameExportUrl) {
        queryClient.setQueryData(key, (previousValue) => {
            if (!previousValue) return previousValue
            if (previousValue?.game) {
                return { ...previousValue, game: { 
                    ...previousValue.game,
                    export_url: gameExportUrl.exportUrl,
                    export_finished_at: gameExportUrl.exportFinishedAt,
                    export_emails: gameExportUrl.exportEmails
                } }
            }
            return { ...previousValue, export_url: gameExportUrl }
        })
        return true
    }

    const gameReelUrl = groupedUpdates.reduce((acc, updateItem) => {
        const isGameReel = updateItem?.message?.source === 'ss.game.reel'
        const reel = updateItem?.message?.detail?.game?.reel
        return isGameReel && reel ? reel : acc
    }, null)

    if (gameReelUrl) {
        queryClient.setQueryData(key, (previousValue) => {
            if (!previousValue) return previousValue
            if (previousValue?.game) {
                return { ...previousValue, game: { ...previousValue.game, reel: gameReelUrl } }
            }
            return { ...previousValue, reel: gameReelUrl }
        })
        return true
    }

    const taskReelUpdate = groupedUpdates.reduce((acc, updateItem) => {
        if (updateItem?.message?.source === 'ss.task.reel') {
            const taskObj = updateItem?.message?.detail?.task
            if (taskObj?.id && taskObj?.reel) {
                return { id: taskObj.id, url: taskObj.reel }
            }
        }
        return acc
    }, null)

    const teamReelUpdate = groupedUpdates.reduce((acc, updateItem) => {
        if (updateItem?.message?.source === 'ss.team.reel') {
            const teamObj = updateItem?.message?.detail?.team
            if (teamObj?.id && teamObj?.reel) {
                return { id: teamObj.id, url: teamObj.reel }
            }
        }
        return acc
    }, null)

    const currentData = queryClient.getQueryData(key)
    const taskArray = currentData?.game?.task_reels || currentData?.task_reels
    const teamArray = currentData?.game?.team_reels || currentData?.team_reels
    const shouldUpdateTask = !!(taskReelUpdate && Array.isArray(taskArray))
    const shouldUpdateTeam = !!(teamReelUpdate && Array.isArray(teamArray))

    if (shouldUpdateTask || shouldUpdateTeam) {
        queryClient.setQueryData(key, (previousValue) => {
            if (!previousValue) return previousValue
            let nextValue = previousValue
            if (shouldUpdateTask) {
                if (nextValue?.game) {
                    nextValue = {
                        ...nextValue,
                        game: {
                            ...nextValue.game,
                            task_reels: (nextValue.game.task_reels || []).map((item) =>
                                item?.id === taskReelUpdate.id ? { ...item, url: taskReelUpdate.url } : item
                            ),
                        },
                    }
                } else {
                    nextValue = {
                        ...nextValue,
                        task_reels: (nextValue.task_reels || []).map((item) =>
                            item?.id === taskReelUpdate.id ? { ...item, url: taskReelUpdate.url } : item
                        ),
                    }
                }
            }
            if (shouldUpdateTeam) {
                if (nextValue?.game) {
                    nextValue = {
                        ...nextValue,
                        game: {
                            ...nextValue.game,
                            team_reels: (nextValue.game.team_reels || []).map((item) =>
                                item?.id === teamReelUpdate.id ? { ...item, url: teamReelUpdate.url } : item
                            ),
                        },
                    }
                } else {
                    nextValue = {
                        ...nextValue,
                        team_reels: (nextValue.team_reels || []).map((item) =>
                            item?.id === teamReelUpdate.id ? { ...item, url: teamReelUpdate.url } : item
                        ),
                    }
                }
            }
            return nextValue
        })
        return true
    }

    return false
}

const useRefreshData = () => {
    const queryClient = useQueryClient()
    const [, setRealTimeUpdates] = useRecoilState(realTimeUpdatesState)

    const refreshData = async (queues, route, gameId) => {
        const updates = Array.isArray(queues) ? queues.filter(Boolean) : [queues].filter(Boolean)
        if (updates.length === 0) return

        // Group by unique query key so each key is invalidated once
        const groups = new Map()
        for (const update of updates) {
            const keyId = JSON.stringify(update.key)
            if (!groups.has(keyId)) {
                groups.set(keyId, { key: update.key, updates: [update] })
            } else {
                groups.get(keyId).updates.push(update)
            }
        }

        for (const { key, updates: groupedUpdates } of groups.values()) {
            const isInfinite = typeof key?.includes === 'function' && key.includes(INFINITE_QUERY_KEY)

            if (isInfinite) {
                // any events related to an infinite query keys
                // need to only invalidate a specific page in the collection.
                // otherwise it could unnecessarily load a whole list of pages to refresh the infinite query data.
                const currentPage = queryClient.getQueryData(key)
                const hasCreate = groupedUpdates.some(u => u?.message?.source?.includes('create'))

                if (hasCreate) {
                    // Refresh only the first page of the collection
                    await queryClient.invalidateQueries(key, {
                        refetchPage: (_, index) => index === 0,
                    })
                    continue
                }

                // Refresh the affected page of the collection for infinite queries
                if (currentPage?.pages && currentPage?.pageParams) {
                    const tokenToIndex = new Map()
                    currentPage.pageParams.forEach((token, idx) => tokenToIndex.set(token, idx))

                    const indices = new Set()
                    for (const u of groupedUpdates) {
                        const { source, detail } = u.message || {}
                        const { entityId, listKey } = getDetailEntity({ source, detail })
                        // Find affected page in the collection based on the entity detail
                        if (!entityId || !listKey) continue
                        const dataToken = currentPage.pages.find(page => page?.[listKey]?.find(item => item.id === entityId))?.token
                        if (dataToken && tokenToIndex.has(dataToken)) {
                            indices.add(tokenToIndex.get(dataToken))
                        }
                    }

                    if (indices.size > 0) {
                        // If dataToken is available, refresh page index in which the dataToken belongs to
                        await queryClient.invalidateQueries(key, {
                            refetchPage: (_, index) => indices.has(index),
                        })
                    } else {
                        // If entity detail is not available, refresh the whole collection
                        await queryClient.invalidateQueries(key)
                    }
                } else {
                    await queryClient.invalidateQueries(key)
                }
            } else {
                // Try to apply instant update events without invalidation; skip invalidation if handled
                const handled = handleInstantUpdateEvents(queryClient, key, groupedUpdates)
                if (handled) continue

                // invalidate query and refetch new data from the server
                await queryClient.invalidateQueries(key)
            }
        }

        // After successful invalidations, remove each update's route from realtime updates
        const eventIds = new Set(updates.map(u => u?.message?.id).filter(Boolean))
        setRealTimeUpdates(prev => prev
            .map(updateItem => {
                // if the event id is not the same (not included in the batch), return the update item
                if (!eventIds.has(updateItem?.message?.id)) {
                    return updateItem
                }

                // if the route is not affected, return the update item
                if (!updateItem?.affectedPaths?.map(p => p.path)?.includes(route)) {
                    return updateItem
                }

                // if the game id is provided, only remove the game id from the shouldShowRealTimeDot map
                if (gameId) {
                    const updatedPaths = updateItem.affectedPaths.map(p => {
                        if (p.path === route) {
                            return { ...p, shouldShowRealTimeDot: { ...p.shouldShowRealTimeDot, [gameId]: false } }
                        }
                        return p
                    })
                    return updatedPaths.length > 0 ? { ...updateItem, affectedPaths: updatedPaths } : null
                }

                // remove the route from the affected paths
                const updatedPaths = updateItem.affectedPaths.filter(p => p.path !== route)
                return updatedPaths.length > 0 ? { ...updateItem, affectedPaths: updatedPaths } : null
            })
            .filter(Boolean)
        )
    }

    return refreshData
}

export default useRefreshData